/**
 * Task Service
 *
 * Handles all task-related operations including CRUD operations,
 * comments, attachments, and activity tracking.
 *
 * @module services/taskService
 */

import { supabase } from './supabase';
import { terminoParaFiltro, columnaNormalizada } from '../utils/normalizacionBusqueda';
import { createNotification } from './notificationService';
import {
  canAssignWithoutStake,
  canAssignInProject,
  canCreateTaskWithoutStake,
  canCreateTaskInProject,
  isInternalStaffRole,
  ASSIGNER_SCOPE_DENIAL_MESSAGE,
  assigneeRoleDenialMessage,
  ASSIGNEE_NOT_FOUND_MESSAGE,
  TASK_CREATE_SCOPE_DENIAL_MESSAGE
} from '../utils/taskAssignmentPermissions';

/**
 * Avisar a los asignados y DECIR si el aviso no salió.
 *
 * Antes esto era `createNotification({...}).catch(err => console.error(...))`
 * en dos lugares: fire-and-forget contra un `console.error` que nadie mira.
 * Combinado con el RETURNING roto de `createNotification` (ver el comentario
 * de esa función), el resultado medido en prod era que la tarea se guardaba,
 * el toast decía "exitosamente" y el técnico no se enteraba nunca:
 * `notifications` tenía CERO filas con `notification_type='task'` contra 14
 * asignaciones cross-user reales (2026-07-30).
 *
 * Se espera el resultado y se devuelve un `warning` al llamador —el mismo
 * contrato que ya usa `createTask` cuando falla la asignación, y que
 * `TaskForm.js` y `TasksPage.js` renderizan con `toast.warning(...)`.
 * La tarea YA está guardada, así que esto nunca es un `error`: avisar mal no
 * puede deshacer trabajo que sí quedó.
 *
 * Exportada desde #555: `quotationService.submitQuotationForReview` asigna la
 * tarea de revisión a TODOS los admins y no notificaba a ninguno — el mismo
 * agujero, en otro archivo del mismo carril. Reimplementarlo allá habría sido
 * una cuarta copia de la misma regla; el `related_type`/`action_url` que la
 * campana necesita para linkear tiene que salir de un solo lugar.
 *
 * @param {string[]} userIds - destinatarios (se saltea el propio emisor)
 * @param {string} senderId - quien asigna
 * @param {Object} payload - campos de la notificación (sin user_id)
 * @returns {Promise<string|null>} mensaje de warning, o null si salió todo
 */
export const notifyAssignees = async (userIds, senderId, payload) => {
  const targets = (userIds || []).filter((id) => id && id !== senderId);
  if (targets.length === 0) return null;

  const results = await Promise.all(
    targets.map(async (userId) => {
      try {
        const { error } = await createNotification({ user_id: userId, ...payload });
        return error || null;
      } catch (err) {
        return err;
      }
    })
  );

  const fallidas = results.filter(Boolean);
  if (fallidas.length === 0) return null;

  return `La tarea se guardó, pero no se pudo notificar a ${fallidas.length} de ${targets.length} ` +
    'persona(s) asignada(s). Avisales por otro medio.';
};

/**
 * Get all tasks with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Task status
 * @param {string} filters.priority - Task priority
 * @param {string} filters.projectId - Project ID
 * @param {string} filters.clientId - Client ID. #791: matchea el cliente del
 *   proyecto de la tarea (`project.client_id`) O el `tasks.client_id` directo
 *   (tareas de prospección sin proyecto real)
 * @param {string} filters.assigneeId - Assignee user ID
 * @param {string} filters.search - Search term (task_number, title y description)
 * @param {string} filters.dueDateFrom - 'YYYY-MM-DD' inclusive (ventana de vencimiento)
 * @param {string} filters.dueDateTo - 'YYYY-MM-DD' inclusive (ventana de vencimiento)
 * @param {boolean} filters.includeNoDueDate - incluir tareas sin `due_date` dentro
 *   de la ventana anterior (la vista Timeline las muestra siempre, agrupadas en
 *   "Sin fecha asignada")
 * @param {number} filters.page - Page number (0-indexed)
 * @param {number} filters.pageSize - Items per page
 * @param {string|null} filters.parentTaskId - If set, return only subtasks of this parent.
 *                                              If omitted (default), only root tasks are returned
 *                                              (parent_task_id IS NULL). Pass the string 'all'
 *                                              to disable the filter entirely.
 * @returns {Promise<{data, count, error}>}
 */
export const listTasks = async (filters = {}) => {
  try {
    // Use !inner join on projects when filtering by a project-relation field
    // (project status). Without !inner, .eq('project.X', ...) only filters
    // the embedded data — parent task rows are still returned with project=null.
    // #791: `clientId` ya NO necesita este !inner — ver el filtro más abajo,
    // que resuelve por project_id IN (...) sobre la propia tabla `tasks`.
    const needsInnerProjectJoin = !!filters.projectStatus;
    const projectJoin = needsInnerProjectJoin
      ? 'project:projects!inner(id, project_number, title, client_id, status)'
      : 'project:projects(id, project_number, title, client_id)';

    let query = supabase
      .from('tasks')
      .select(`
        *,
        ${projectJoin},
        client:clients!tasks_client_id_fkey(id, company_name),
        assignees:task_assignments(
          id,
          user_id,
          assigned_user:profiles!task_assignments_user_id_fkey(id, full_name)
        )
      `, { count: 'exact' });

    // Parent/subtask filter — by default hide subtasks from the global list.
    if (filters.parentTaskId === 'all') {
      // explicit opt-out: return roots + subtasks
    } else if (filters.parentTaskId) {
      query = query.eq('parent_task_id', filters.parentTaskId);
    } else {
      query = query.is('parent_task_id', null);
    }

    // Apply filters
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.priority) {
      query = query.eq('priority', filters.priority);
    }

    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }

    // Filter by project status (e.g., 'quoted' for pending quotations)
    if (filters.projectStatus) {
      query = query.eq('project.status', filters.projectStatus);
    }

    // Filter by task category
    if (filters.category) {
      query = query.eq('category', filters.category);
    }

    // Filter by client — #791: una tarea puede tener cliente por DOS vías, y
    // las dos cuentan. La de siempre: el cliente de su proyecto
    // (`project.client_id`). La nueva: `tasks.client_id` directo, para
    // prospección/seguimiento SIN proyecto real (la tarea vive en el
    // contenedor personal). Filtrar sólo por la primera —lo que hacía este
    // código antes— dejaba exactamente afuera el caso que el issue #791 pidió
    // poder ver por cliente.
    //
    // Las dos vías se resuelven a ids con consultas que arma el cliente de
    // Supabase (`.eq` / `.in` con arrays) y se cruzan con `.in('id', ...)`,
    // igual que el filtro por asignado de abajo. NO se arma un `.or()` con el
    // id interpolado: dentro de un filtro de PostgREST `,` `(` `)` son sintaxis,
    // y la guarda de #742 (`guardaFiltrosInterpolados742`) sólo deja bajar ese
    // techo, nunca subirlo.
    if (filters.clientId) {
      const { data: clientProjects, error: clientProjectsError } = await supabase
        .from('projects')
        .select('id')
        .eq('client_id', filters.clientId);
      if (clientProjectsError) throw clientProjectsError;
      const clientProjectIds = (clientProjects || []).map((p) => p.id);

      const { data: directTasks, error: directTasksError } = await supabase
        .from('tasks')
        .select('id')
        .eq('client_id', filters.clientId);
      if (directTasksError) throw directTasksError;

      let projectTaskIds = [];
      if (clientProjectIds.length > 0) {
        const { data: projectTasks, error: projectTasksError } = await supabase
          .from('tasks')
          .select('id')
          .in('project_id', clientProjectIds);
        if (projectTasksError) throw projectTasksError;
        projectTaskIds = (projectTasks || []).map((t) => t.id);
      }

      const clientTaskIds = [...new Set([...(directTasks || []).map((t) => t.id), ...projectTaskIds])];
      if (clientTaskIds.length === 0) {
        return { data: [], count: 0, error: null };
      }
      query = query.in('id', clientTaskIds);
    }

    if (filters.assigneeId) {
      // Get task IDs assigned to this user via task_assignments
      const { data: assignments, error: assignError } = await supabase
        .from('task_assignments')
        .select('task_id')
        .eq('user_id', filters.assigneeId);

      if (assignError) throw assignError;

      const assignedTaskIds = assignments?.map(a => a.task_id) || [];
      if (assignedTaskIds.length === 0) {
        return { data: [], count: 0, error: null };
      }
      query = query.in('id', assignedTaskIds);
    }

    if (filters.search) {
      // `description` entra acá a propósito. Las vistas de /tareas filtraban por
      // descripción en JS DESPUÉS de recibir la página de 20 filas, así que una
      // coincidencia en la fila 40 no existía para el buscador y el `count` que
      // alimenta el paginador no correspondía al conjunto mostrado. Filtrar es
      // trabajo del query; la página se recorta después.
      //
      // #742/#739 — el término sale del módulo compartido: normaliza tildes,
      // saca los metacaracteres de PostgREST y escapa los comodines de LIKE.
      // `title` y `description` van contra sus columnas generadas; `task_number`
      // se queda crudo porque es un código (`T-Q102-001`), no texto humano.
      //
      // El filtro se queda EN LA BASE por el motivo que dice el comentario de
      // arriba: la página se recorta después, así que un filtro en JS volvería a
      // producir el defecto que ese comentario describe.
      const termino = terminoParaFiltro(filters.search);
      query = query.or(
        `task_number.ilike.%${termino}%,`
        + `${columnaNormalizada('title')}.ilike.%${termino}%,`
        + `${columnaNormalizada('description')}.ilike.%${termino}%`
      );
    }

    // Ventana de vencimiento (la usa el selector Mes/Año del Timeline).
    //
    // Esto vivía en el componente, aplicado sobre las 20 filas ya recortadas: si
    // las 20 tareas más recientes vencían en agosto, elegir julio mostraba "No
    // hay tareas para Julio" aunque hubiera decenas. Filtrar tiene que pasar
    // ANTES de `.range()`, nunca después.
    if (filters.dueDateFrom || filters.dueDateTo) {
      const window = [];
      if (filters.dueDateFrom) window.push(`due_date.gte.${filters.dueDateFrom}`);
      if (filters.dueDateTo) window.push(`due_date.lte.${filters.dueDateTo}`);

      if (filters.includeNoDueDate) {
        query = query.or(`due_date.is.null,and(${window.join(',')})`);
      } else {
        if (filters.dueDateFrom) query = query.gte('due_date', filters.dueDateFrom);
        if (filters.dueDateTo) query = query.lte('due_date', filters.dueDateTo);
      }
    }

    // Apply pagination
    const page = filters.page || 0;
    const pageSize = filters.pageSize || 20;
    const from = page * pageSize;
    const to = from + pageSize - 1;

    query = query
      .range(from, to)
      .order('created_at', { ascending: false });

    const { data, count, error } = await query;

    if (error) throw error;

    return { data, count, error: null };
  } catch (error) {
    console.error('List tasks error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get task by ID with all related data
 * @param {string} taskId - Task ID
 * @returns {Promise<{data, error}>}
 */
export const getTask = async (taskId) => {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .select(`
        *,
        project:projects(id, project_number, title, quoted_amount, client_id),
        client:clients!tasks_client_id_fkey(id, company_name),
        assignees:task_assignments(
          id,
          user_id,
          assigned_user:profiles!task_assignments_user_id_fkey(id, full_name)
        )
      `)
      .eq('id', taskId)
      .single();

    if (error) throw error;

    // Self-join for parent is brittle in PostgREST; fetch it separately when set.
    if (data?.parent_task_id) {
      const { data: parent } = await supabase
        .from('tasks')
        .select('id, task_number, title')
        .eq('id', data.parent_task_id)
        .single();
      data.parent = parent || null;
    } else if (data) {
      data.parent = null;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Get task error:', error);
    return { data: null, error };
  }
};

/**
 * Get subtasks of a given parent task.
 * Mirrors listTasks but always filters by parent_task_id and returns a simple
 * ordered list (no pagination).
 *
 * @param {string} parentTaskId
 * @returns {Promise<{data, error}>}
 */
export const getSubtasks = async (parentTaskId) => {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .select(`
        *,
        client:clients!tasks_client_id_fkey(id, company_name),
        assignees:task_assignments(
          id,
          user_id,
          assigned_user:profiles!task_assignments_user_id_fkey(id, full_name)
        )
      `)
      .eq('parent_task_id', parentTaskId)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get subtasks error:', error);
    return { data: null, error };
  }
};

/**
 * Read the aggregated rollup stats for a (parent) task from the
 * `task_rollup_stats` view.
 *
 * @param {string} taskId
 * @returns {Promise<{data, error}>}
 */
export const getTaskRollupStats = async (taskId) => {
  try {
    const { data, error } = await supabase
      .from('task_rollup_stats')
      .select('*')
      .eq('task_id', taskId)
      .maybeSingle();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get task rollup stats error:', error);
    return { data: null, error };
  }
};

/**
 * Create new task
 * @param {Object} taskData - Task data
 * @param {string} [taskData.project_id] - Project ID. #791: opcional — vacío o
 *   ausente resuelve a la lista personal del usuario (RPC
 *   `get_or_create_personal_project`).
 * @param {string|null} [taskData.client_id] - Cliente al que se refiere la
 *   tarea (#791), NULLABLE. '' se normaliza a null.
 * @param {string} taskData.title - Task title
 * @param {string} taskData.description - Task description
 * @param {string} taskData.status - Task status
 * @param {string} taskData.priority - Task priority
 * @param {Date} taskData.due_date - Due date
 * @param {number} taskData.estimated_hours - Estimated hours
 * @param {Array<string>} taskData.assignees - Array of user IDs to assign
 * @returns {Promise<{data, error}>}
 */
/**
 * ¿Puede crear una tarea en ESTE proyecto? (#536) — paridad con
 * `mcp-server/src/tools/taskTools.js` (`assertTaskCreationAllowed`).
 *
 * Este carril es el que estaba de verdad abierto. El carril MCP fallaba antes
 * por otro motivo (`generateTaskNumber` lee `projects` y choca contra el techo
 * de lectura de #488, contestando "ese proyecto no existe"); acá el insert es
 * directo — `task_number` lo pone el trigger `set_task_number` — así que la
 * única barrera era `technician_create_tasks`, que no acota por proyecto.
 *
 * La ley es el techo `tasks_ceiling_insert`
 * (`supabase/migrations/20260730000000_techo_creacion_tareas.sql`). Esto lo
 * explica antes del rebote 42501, que no orienta a nadie.
 *
 * Falla CERRADO: si no se puede resolver el ámbito, no se crea la tarea.
 *
 * @param {string} projectId
 * @param {string} createdBy
 * @throws {Error} con el mensaje que ve el usuario
 */
const assertTaskCreationAllowed = async (projectId, createdBy) => {
  // Sin proyecto no hay ámbito de proyecto que verificar. No puede pasar: para
  // cuando esto corre, `createTask` ya resolvió "sin proyecto" al contenedor
  // personal (#791) — `tasks.project_id` sigue siendo NOT NULL. Se deja el
  // early-return igual, por si algún llamador interno pasa un id vacío por
  // error: fallar acá por un dato ausente sería negar de más por un caso que
  // la tabla ya prohíbe.
  if (!projectId) return;

  const { data: profile } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', createdBy)
    .maybeSingle();

  const callerRole = profile?.role || null;
  if (canCreateTaskWithoutStake(callerRole)) return;

  const { data: stake, error: stakeError } = await supabase.rpc('has_project_stake', {
    p_project_id: projectId,
    p_user_id: createdBy
  });

  if (stakeError || !canCreateTaskInProject(callerRole, stake === true)) {
    throw new Error(TASK_CREATE_SCOPE_DENIAL_MESSAGE);
  }
};

export const createTask = async (taskData) => {
  try {
    const { assignees, ...taskFields } = taskData;

    // #791 — `client_id` es NULLABLE en la base; '' no es un uuid válido para
    // esa FK. El formulario manda '' para "sin cliente", nunca debe llegar
    // crudo al insert.
    if (taskFields.client_id === '') {
      taskFields.client_id = null;
    }

    // #791 — "sin proyecto" se resuelve al contenedor personal
    // `PERSONAL-{auth.uid()}` vía la RPC `get_or_create_personal_project()` de
    // la base, ANTES de la compuerta y del insert. No se arma el
    // `project_number` a mano en JS (`PERSONAL-${userId}`) porque el
    // supervisor NO tiene policy de INSERT en `projects`: la RPC es
    // SECURITY DEFINER y hace ese INSERT por él la primera vez. Falla CERRADO
    // — si la RPC no resuelve, no se crea la tarea con un project_id vacío
    // (la columna sigue NOT NULL).
    if (!taskFields.project_id) {
      const { data: personalProjectId, error: personalProjectError } = await supabase.rpc(
        'get_or_create_personal_project'
      );

      if (personalProjectError) {
        console.error('[CREATE_TASK] No se pudo resolver la lista personal:', personalProjectError);
        return {
          data: null,
          error: new Error(
            'No se pudo crear tu lista personal de tareas: '
            + (personalProjectError.message || 'error desconocido')
            + '. Elegí un proyecto o intentá de nuevo.'
          ),
        };
      }

      taskFields.project_id = personalProjectId;
    }

    console.log('[CREATE_TASK] Creating task with fields:', taskFields);

    // COMPUERTA #536 — antes del insert. Ver `assertTaskCreationAllowed`.
    await assertTaskCreationAllowed(taskFields.project_id, taskFields.created_by);

    // Create task
    const { data: task, error: taskError } = await supabase
      .from('tasks')
      .insert([taskFields])
      .select()
      .single();

    if (taskError) {
      console.error('[CREATE_TASK] Task creation failed:', taskError);
      throw taskError;
    }

    console.log('[CREATE_TASK] Task created successfully:', task.id);

    // Assign users if provided
    if (assignees && assignees.length > 0) {
      // Get current user ID from task created_by
      const currentUserId = taskFields.created_by;

      const assignments = assignees.map(userId => ({
        task_id: task.id,
        user_id: userId,
        assigned_by: currentUserId, // Required by RLS policy
      }));

      console.log('[CREATE_TASK] Creating assignments:', assignments);

      const { error: assignError } = await supabase
        .from('task_assignments')
        .insert(assignments);

      if (assignError) {
        console.error('[CREATE_TASK] Assignment creation failed:', assignError);
        // La tarea YA existe: tirar acá hacía que la UI dijera "Error al crear
        // la tarea" sobre una tarea que sí quedó guardada, y el usuario la
        // creaba de nuevo. Se devuelve la tarea con un aviso explícito de lo
        // único que falló. (Pasa, por ejemplo, con un supervisor: tiene
        // `supervisor_create_tasks` pero ninguna policy de INSERT en
        // `task_assignments`.)
        return {
          data: task,
          error: null,
          warning:
            'La tarea se creó, pero no se pudieron guardar las asignaciones: no tienes permiso para asignar.',
        };
      }

      console.log('[CREATE_TASK] Assignments created successfully');

      // Avisar a los asignados, y decirlo si no se pudo (ver notifyAssignees).
      const notifyWarning = await notifyAssignees(assignees, currentUserId, {
        notification_type: 'task',
        title: 'Nueva tarea asignada',
        message: `Se te ha asignado la tarea: ${task.title}`,
        action_url: `/tareas/${task.id}`,
        related_type: 'task',
        related_id: task.id,
      });

      if (notifyWarning) {
        return { data: task, error: null, warning: notifyWarning };
      }
    }

    return { data: task, error: null };
  } catch (error) {
    console.error('[CREATE_TASK] Error:', error);
    return { data: null, error };
  }
};

/**
 * #678 — mismo criterio que `mcp-server/src/utils/consistenciaDeTarea.js`
 * (carriles HTTP y stdio del MCP): `progress_percentage: 100` conviviendo con
 * un `status` no terminal se aceptaba en silencio, y `get_project_summary` /
 * el resumen de la obra cuentan por `status`, así que una tarea "terminada"
 * seguía apareciendo como pendiente.
 *
 * No se fuerza la transición acá tampoco, por el mismo motivo que en el MCP:
 * una tarea-compuerta de aprobación (`material_request_id` sin aprobar) tiene
 * un trigger de base que RECHAZA `status: 'completed'` hasta que la solicitud
 * esté aprobada, y forzar la escritura convertiría un simple "cargué el
 * progreso" en un error sobre una solicitud de materiales que nadie mencionó.
 * Esta app es una aplicación distinta del `mcp-server/` y no puede importar
 * ese módulo — la lógica se repite acá a propósito, no por descuido.
 *
 * @param {Object} task - la fila de `tasks` YA actualizada.
 * @returns {string|null} un único aviso combinado, o null si no hay nada que decir.
 */
const avisoDeConsistenciaDeTarea = (task) => {
  if (!task) return null;
  const avisos = [];
  const ESTADOS_TERMINALES = ['completed', 'cancelled'];

  if (
    (task.progress_percentage === 100 || task.progress_percentage === '100')
    && task.status
    && !ESTADOS_TERMINALES.includes(task.status)
  ) {
    avisos.push(
      `progress_percentage llegó a 100 pero status sigue en "${task.status}". `
      + 'Esta tarea va a seguir contando como pendiente (o en curso) en el resumen de la obra '
      + 'hasta que status también pase a "Completada".'
    );
  }

  const estimadas = task.estimated_hours;
  const reales = task.actual_hours;
  if (estimadas !== null && estimadas !== undefined && estimadas !== ''
    && reales !== null && reales !== undefined && reales !== '') {
    const estimadasNum = Number(estimadas);
    const realesNum = Number(reales);
    // Mismo múltiplo que el MCP (3x): deja pasar el sobrecosto normal de obra
    // y marca el caso real medido en #678 (9999 contra una estimación de 4).
    if (Number.isFinite(estimadasNum) && Number.isFinite(realesNum)
      && estimadasNum > 0 && realesNum > estimadasNum * 3) {
      avisos.push(
        `Las horas reales (${realesNum}) son más de 3x la estimación (${estimadasNum}). `
        + 'Se guardó tal cual — si es un error de tipeo, corregilo antes de que esta cifra se use para facturar.'
      );
    }
  }

  return avisos.length ? avisos.join(' ') : null;
};

/**
 * Update task
 * @param {string} taskId - Task ID
 * @param {Object} updates - Task updates
 * @param {string|null} [updates.client_id] - #791: NULLABLE — `null` desvincula
 *   el cliente de la tarea; '' se normaliza a null.
 * @param {string} [updates.project_id] - #791: '' resuelve a la lista personal
 *   del usuario (misma RPC que `createTask`).
 * @returns {Promise<{data, error}>}
 */
export const updateTask = async (taskId, updates) => {
  try {
    const cleanUpdates = { ...updates };

    // #791 — mismo saneado que createTask: '' no es un uuid válido para
    // `client_id`. `null` sí es válido y es justo cómo se desvincula el
    // cliente de la tarea (el trigger de la base lo permite: sólo rechaza
    // cuando HAY client_id y no coincide con el del proyecto).
    if ('client_id' in cleanUpdates && cleanUpdates.client_id === '') {
      cleanUpdates.client_id = null;
    }

    // #791 — si el formulario manda `project_id: ''` es porque el usuario
    // quitó el proyecto: en este modelo eso significa "mandala a mi lista
    // personal" (`tasks.project_id` sigue NOT NULL), nunca un uuid vacío
    // contra la FK. Se resuelve con la misma RPC que createTask.
    if ('project_id' in cleanUpdates && !cleanUpdates.project_id) {
      const { data: personalProjectId, error: personalProjectError } = await supabase.rpc(
        'get_or_create_personal_project'
      );

      if (personalProjectError) {
        console.error('[UPDATE_TASK] No se pudo resolver la lista personal:', personalProjectError);
        return {
          data: null,
          error: new Error(
            'No se pudo mover la tarea a tu lista personal: '
            + (personalProjectError.message || 'error desconocido')
          ),
        };
      }

      cleanUpdates.project_id = personalProjectId;
    }

    const { data, error } = await supabase
      .from('tasks')
      .update(cleanUpdates)
      .eq('id', taskId)
      .select()
      .single();

    if (error) throw error;

    const warning = avisoDeConsistenciaDeTarea(data);
    return { data, error: null, ...(warning ? { warning } : {}) };
  } catch (error) {
    console.error('Update task error:', error);
    return { data: null, error };
  }
};

/**
 * Update task status
 * @param {string} taskId - Task ID
 * @param {string} status - New status
 * @returns {Promise<{data, error}>}
 */
export const updateTaskStatus = async (taskId, status) => {
  return updateTask(taskId, { status });
};

/**
 * Reorder task within Kanban board
 * Uses database function for atomic reordering
 * @param {string} taskId - Task ID
 * @param {number} newPosition - New position in column (0-indexed)
 * @param {string|null} newStatus - New status if moving between columns (optional)
 * @returns {Promise<{data, error}>}
 */
export const reorderTask = async (taskId, newPosition, newStatus = null) => {
  try {
    const { error } = await supabase.rpc('reorder_task', {
      p_task_id: taskId,
      p_new_position: newPosition,
      p_new_status: newStatus,
    });

    if (error) throw error;

    return { data: true, error: null };
  } catch (error) {
    console.error('Reorder task error:', error);
    return { data: null, error };
  }
};

/**
 * Delete task
 * @param {string} taskId - Task ID
 * @returns {Promise<{data, error}>}
 */
export const deleteTask = async (taskId) => {
  try {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (error) throw error;

    return { data: true, error: null };
  } catch (error) {
    console.error('Delete task error:', error);
    return { data: null, error };
  }
};

/**
 * Add comment to task with optional image attachments (Slack/Discord style)
 * @param {string} taskId - Task ID
 * @param {string} content - Comment content
 * @param {string} userId - User ID
 * @param {Array<Object>} attachments - Optional array of attachment objects
 * @returns {Promise<{data, error}>}
 */
export const addTaskComment = async (taskId, content, userId, attachments = []) => {
  try {
    const { data, error } = await supabase
      .from('task_comments')
      .insert([{
        task_id: taskId,
        created_by: userId,
        comment: content,
        attachment_urls: attachments,
      }])
      .select('*')
      .single();

    if (error) throw error;

    // Fetch user profile
    if (data) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('id', data.created_by)
        .single();
      data.user = profile;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Add task comment error:', error);
    return { data: null, error };
  }
};

/**
 * Upload comment attachment to Supabase storage
 * @param {string} taskId - Task ID
 * @param {File} file - File to upload
 * @returns {Promise<{data, error}>}
 */
export const uploadCommentAttachment = async (taskId, file) => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `comment-attachments/${taskId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('task-attachments')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('task-attachments')
      .getPublicUrl(filePath);

    return {
      data: {
        url: publicUrl,
        filename: file.name,
        size: file.size,
        mimetype: file.type,
        uploaded_at: new Date().toISOString(),
      },
      error: null,
    };
  } catch (error) {
    console.error('Upload comment attachment error:', error);
    return { data: null, error };
  }
};

/**
 * Get task comments
 * @param {string} taskId - Task ID
 * @returns {Promise<{data, error}>}
 */
export const getTaskComments = async (taskId) => {
  try {
    // Simplified query - will fetch user data separately if needed
    const { data, error } = await supabase
      .from('task_comments')
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Fetch user profiles for each comment
    if (data && data.length > 0) {
      const userIds = [...new Set(data.map(c => c.created_by))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url')
        .in('id', userIds);

      const profileMap = (profiles || []).reduce((map, p) => {
        map[p.id] = p;
        return map;
      }, {});

      data.forEach(comment => {
        comment.user = profileMap[comment.created_by] || null;
      });
    }

    return { data, error: null };
  } catch (error) {
    console.error('Get task comments error:', error);
    return { data: null, error };
  }
};

/**
 * Update comment
 * @param {string} commentId - Comment ID
 * @param {string} content - New content
 * @returns {Promise<{data, error}>}
 */
export const updateTaskComment = async (commentId, content) => {
  try {
    const { data, error } = await supabase
      .from('task_comments')
      .update({ comment: content, edited: true })
      .eq('id', commentId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Update task comment error:', error);
    return { data: null, error };
  }
};

/**
 * Delete comment
 * @param {string} commentId - Comment ID
 * @returns {Promise<{data, error}>}
 */
export const deleteTaskComment = async (commentId) => {
  try {
    const { error } = await supabase
      .from('task_comments')
      .delete()
      .eq('id', commentId);

    if (error) throw error;

    return { data: true, error: null };
  } catch (error) {
    console.error('Delete task comment error:', error);
    return { data: null, error };
  }
};

/**
 * Add attachment to task
 * @param {string} taskId - Task ID
 * @param {File} file - File to upload
 * @param {string} userId - User ID
 * @returns {Promise<{data, error}>}
 */
export const addTaskAttachment = async (taskId, file, userId) => {
  try {
    // Upload file to storage
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `task-attachments/${taskId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('task-attachments')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('task-attachments')
      .getPublicUrl(filePath);

    // Save attachment record
    const { data, error } = await supabase
      .from('task_attachments')
      .insert([{
        task_id: taskId,
        file_name: file.name,
        file_url: publicUrl,
        file_type: file.type,
        file_size: file.size,
        uploaded_by: userId,
      }])
      .select(`
        *,
        uploaded_by_profile:profiles(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Add task attachment error:', error);
    return { data: null, error };
  }
};

/**
 * Delete attachment
 * @param {string} attachmentId - Attachment ID
 * @returns {Promise<{data, error}>}
 */
export const deleteTaskAttachment = async (attachmentId) => {
  try {
    // Get attachment details first
    const { data: attachment, error: getError } = await supabase
      .from('task_attachments')
      .select('file_url')
      .eq('id', attachmentId)
      .single();

    if (getError) throw getError;

    // Extract file path from URL
    const url = new URL(attachment.file_url);
    const filePath = url.pathname.split('/').slice(-3).join('/');

    // Delete from storage
    const { error: deleteFileError } = await supabase.storage
      .from('task-attachments')
      .remove([filePath]);

    if (deleteFileError) throw deleteFileError;

    // Delete record
    const { error } = await supabase
      .from('task_attachments')
      .delete()
      .eq('id', attachmentId);

    if (error) throw error;

    return { data: true, error: null };
  } catch (error) {
    console.error('Delete task attachment error:', error);
    return { data: null, error };
  }
};

/**
 * Get task activity log
 * @param {string} taskId - Task ID
 * @returns {Promise<{data, error}>}
 */
export const getTaskActivity = async (taskId) => {
  try {
    const { data, error } = await supabase
      .from('task_activity')
      .select(`
        *,
        user:profiles(id, full_name)
      `)
      .eq('task_id', taskId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get task activity error:', error);
    return { data: null, error };
  }
};

/**
 * ¿Quién asigna, y a quién? (#534) — paridad con
 * `mcp-server/src/tools/taskTools.js` (`assertAssignmentAllowed`).
 *
 * Se corre ANTES de tocar `task_assignments`, y el orden importa: la fila que
 * se está por escribir es exactamente la que convertiría "¿participa del
 * proyecto?" en un sí. Un técnico que se autoasignaba una tarea ajena quedaba
 * con `has_project_stake()` en true sobre el proyecto entero, y desde ahí los
 * techos de #488/#523 lo dejaban pasar por la puerta.
 *
 * La ley es la RLS (`20260729110000_techo_asignaciones.sql`); esto es lo que lo
 * explica antes del rebote 42501, que no orienta a nadie.
 *
 * Falla CERRADO: si no se puede leer la tarea o el ámbito, no se asigna.
 *
 * @param {string} taskId
 * @param {string} assignedBy       quien asigna
 * @param {Array<string>} toAdd     ids que van a quedar asignados
 * @throws {Error} con el mensaje que ve el usuario
 */
const assertAssignmentAllowed = async (taskId, assignedBy, toAdd) => {
  const { data: profile } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', assignedBy)
    .maybeSingle();

  const callerRole = profile?.role || null;

  // --- Regla 2: a quién. Sin excepción por rol de quien asigna: un `client`
  // como assignee de una tarea interna es un DATO inválido, no un permiso.
  if (toAdd.length > 0) {
    const { data: assignees, error: assigneesError } = await supabase
      .from('profiles')
      .select('id, role')
      .in('id', toAdd);

    if (assigneesError) throw assigneesError;

    const byId = new Map((assignees || []).map((p) => [p.id, p.role]));
    for (const id of toAdd) {
      if (!byId.has(id)) throw new Error(ASSIGNEE_NOT_FOUND_MESSAGE);
      if (!isInternalStaffRole(byId.get(id))) {
        throw new Error(assigneeRoleDenialMessage(byId.get(id)));
      }
    }
  }

  // --- Regla 1: quién asigna.
  if (canAssignWithoutStake(callerRole)) return;

  const { data: task } = await supabase
    .from('tasks')
    .select('id, project_id')
    .eq('id', taskId)
    .maybeSingle();

  if (!task) throw new Error(ASSIGNER_SCOPE_DENIAL_MESSAGE);

  const { data: stake, error: stakeError } = await supabase.rpc('has_project_stake', {
    p_project_id: task.project_id,
    p_user_id: assignedBy
  });

  if (stakeError || !canAssignInProject(callerRole, stake === true)) {
    throw new Error(ASSIGNER_SCOPE_DENIAL_MESSAGE);
  }
};

/**
 * Assign users to task
 * @param {string} taskId - Task ID
 * @param {Array<string>} userIds - Array of user IDs
 * @param {string} assignedBy - User ID who is doing the assignment (defaults to current user)
 * @returns {Promise<{data, error}>}
 */
export const assignTaskUsers = async (taskId, userIds, assignedBy = null) => {
  try {
    // Get current user if assignedBy not provided
    if (!assignedBy) {
      const { data: { user } } = await supabase.auth.getUser();
      assignedBy = user?.id;
    }

    const desired = [...new Set((userIds || []).filter(Boolean))];
    let notifyWarning = null;

    // Estado actual. Se compara contra el deseado en vez de borrar todo y
    // volver a insertar, por dos razones concretas:
    //
    //   1. El DELETE está acotado por RLS (`employee_delete_assignments`:
    //      `is_employee() AND assigned_by = auth.uid()`). Un borrado que la
    //      policy filtra NO devuelve error: devuelve cero filas. El código
    //      viejo hacía `await supabase...delete()` sin leer el resultado y
    //      después retornaba `{ data: true }` incondicionalmente, así que
    //      desasignar mostraba el toast verde "Asignaciones actualizadas" y
    //      al recargar seguían todos ahí.
    //   2. Borrar-y-reinsertar a los que no cambiaron los expone a chocar
    //      contra la unique `(task_id, user_id, role)` cuando el DELETE fue
    //      filtrado. Tocando sólo el delta, no cambiar nada es un no-op real.
    const { data: current, error: readError } = await supabase
      .from('task_assignments')
      .select('id, user_id')
      .eq('task_id', taskId);

    if (readError) throw readError;

    const currentRows = current || [];
    const currentIds = currentRows.map((a) => a.user_id);
    const toRemove = currentRows.filter((a) => !desired.includes(a.user_id));
    const toAdd = desired.filter((id) => !currentIds.includes(id));

    // Compuerta #534. Va después del delta y antes de escribir nada: una
    // llamada que no cambia nada no tiene por qué fallar, pero cualquier
    // mutación del conjunto —alta o baja— pasa por acá.
    if (toAdd.length > 0 || toRemove.length > 0) {
      await assertAssignmentAllowed(taskId, assignedBy, toAdd);
    }

    if (toRemove.length > 0) {
      const { data: removed, error: deleteError } = await supabase
        .from('task_assignments')
        .delete()
        .in('id', toRemove.map((a) => a.id))
        .select('id');

      if (deleteError) throw deleteError;

      // `.select()` devuelve exactamente las filas que la RLS dejó borrar.
      // Si faltan, la operación NO se completó y decirlo es obligatorio.
      if ((removed || []).length !== toRemove.length) {
        throw new Error(
          'No se pudieron quitar todas las asignaciones: sólo puede desasignar quien hizo la asignación (o un admin).'
        );
      }
    }

    if (toAdd.length > 0) {
      const assignments = toAdd.map(userId => ({
        task_id: taskId,
        user_id: userId,
        assigned_by: assignedBy, // Required by RLS policy
      }));

      const { data: inserted, error } = await supabase
        .from('task_assignments')
        .insert(assignments)
        .select('id');

      if (error) throw error;

      if ((inserted || []).length !== toAdd.length) {
        throw new Error('No se pudieron guardar todas las asignaciones.');
      }

      // Fetch task title for notification message
      const { data: taskData } = await supabase
        .from('tasks')
        .select('title')
        .eq('id', taskId)
        .single();

      // Avisar a los nuevos asignados, y decirlo si no se pudo.
      notifyWarning = await notifyAssignees(toAdd, assignedBy, {
        notification_type: 'task',
        title: 'Nueva tarea asignada',
        message: `Se te ha asignado la tarea: ${taskData?.title || 'Sin título'}`,
        action_url: `/tareas/${taskId}`,
        related_type: 'task',
        related_id: taskId,
      });
    }

    // `data: true` sólo después de haber verificado fila por fila que la base
    // aceptó el borrado y el alta. Antes se devolvía siempre, sin mirar nada.
    if (notifyWarning) {
      return { data: true, error: null, warning: notifyWarning };
    }
    return { data: true, error: null };
  } catch (error) {
    console.error('Assign task users error:', error);
    return { data: null, error };
  }
};

/**
 * Link material to task.
 *
 * For status='consumed' this routes through the batch-aware RPCs so the
 * consumption correctly decrements inventory_batches (FIFO or manual pick)
 * and records stock_movements with batch_id + unit_cost_at_move. For other
 * statuses it's a plain insert into task_materials.
 *
 * @param {string} taskId - Task ID
 * @param {Object} materialData
 * @param {string} materialData.inventory_item_id - Inventory item ID
 * @param {number} materialData.quantity - Quantity
 * @param {number} [materialData.unit_cost] - Unit cost snapshot (used for non-consumed rows)
 * @param {string} materialData.status - planned | reserved | consumed | returned
 * @param {string} [materialData.notes]
 * @param {Array<{batchId:string, quantity:number}>} [materialData.batch_selections]
 *        If provided with status=consumed, consumes from these specific batches
 *        (manual override). Otherwise consumed status falls back to automatic FIFO.
 * @returns {Promise<{data, error}>}
 */
export const linkTaskMaterial = async (taskId, materialData) => {
  try {
    const {
      inventory_item_id,
      quantity,
      unit_cost,
      status,
      notes,
      batch_selections,
    } = materialData;

    // Branch 1 — consumed: go through FIFO / manual-batch RPC first.
    if (status === 'consumed') {
      const hasManualBatches = Array.isArray(batch_selections) && batch_selections.length > 0;
      let rpcTotalCost = 0;
      let rpcMovementsCount = 0;
      let primaryMovementId = null;

      if (hasManualBatches) {
        const { data: rpcData, error: rpcError } = await supabase.rpc('consume_specific_batches', {
          p_inventory_id: inventory_item_id,
          p_batch_selections: batch_selections.map((b) => ({
            batch_id: b.batchId,
            quantity: b.quantity,
          })),
          p_project_id: null,
          p_task_id: taskId,
          p_reason: 'task_consumption',
          p_notes: notes || null,
          p_created_by: null,
        });
        if (rpcError) throw rpcError;
        if (!rpcData?.success) {
          throw new Error(rpcData?.message || 'No se pudo consumir los lotes seleccionados');
        }
        rpcTotalCost = Number(rpcData.total_cost) || 0;
        rpcMovementsCount = Number(rpcData.movements_created) || 0;
        primaryMovementId = rpcData.batches_affected?.[0]?.movement_id || null;
      } else {
        const { data: rpcRows, error: rpcError } = await supabase.rpc('consume_inventory_fifo', {
          p_inventory_id: inventory_item_id,
          p_quantity: quantity,
          p_project_id: null,
          p_task_id: taskId,
          p_reason: 'task_consumption',
          p_notes: notes || null,
          p_created_by: null,
        });
        if (rpcError) throw rpcError;
        const row = Array.isArray(rpcRows) ? rpcRows[0] : rpcRows;
        if (!row?.success) {
          throw new Error(row?.message || 'Stock insuficiente para consumir este material');
        }
        rpcTotalCost = Number(row.total_cost) || 0;
        rpcMovementsCount = Number(row.movements_created) || 0;
        primaryMovementId = row.batches_affected?.[0]?.movement_id || null;
      }

      const weightedAvgCost = quantity > 0 ? rpcTotalCost / quantity : (Number(unit_cost) || null);

      const { data, error } = await supabase
        .from('task_materials')
        .insert([{
          task_id: taskId,
          inventory_id: inventory_item_id,
          quantity_required: quantity,
          quantity_used: quantity,
          status: 'consumed',
          stock_movement_id: primaryMovementId,
          unit_cost_snapshot: weightedAvgCost,
          notes: notes || null,
        }])
        .select(`
          *,
          inventory_item:inventory(id, name, unit, sku)
        `)
        .single();

      if (error) throw error;

      return {
        data: { ...data, _fifo_summary: { total_cost: rpcTotalCost, movements: rpcMovementsCount } },
        error: null,
      };
    }

    // Branch 2 — planned / reserved / returned: plain insert, no inventory movement.
    const { data, error } = await supabase
      .from('task_materials')
      .insert([{
        task_id: taskId,
        inventory_id: inventory_item_id,
        quantity_required: quantity,
        quantity_used: 0,
        status,
        unit_cost_snapshot: Number(unit_cost) || null,
        notes: notes || null,
      }])
      .select(`
        *,
        inventory_item:inventory(id, name, unit, sku)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Link task material error:', error);
    return { data: null, error };
  }
};

/**
 * Add material to task (alias for linkTaskMaterial)
 * @param {string} taskId - Task ID
 * @param {Object} materialData - Material data
 * @returns {Promise<{data, error}>}
 */
export const addTaskMaterial = linkTaskMaterial;

/**
 * Get task materials
 * @param {string} taskId - Task ID
 * @returns {Promise<{data, error}>}
 */
export const getTaskMaterials = async (taskId) => {
  try {
    const { data, error } = await supabase
      .from('task_materials')
      .select(`
        *,
        inventory_item:inventory(id, name, unit, sku, current_stock)
      `)
      .eq('task_id', taskId)
      // `task_materials` NO tiene `created_at`: la columna de alta es
      // `added_at`. Ordenar por la inexistente hacía fallar el query entero
      // con 42703 (undefined_column), así que la pestaña Materiales no podía
      // mostrar nada aunque hubiera filas.
      .order('added_at', { ascending: false });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get task materials error:', error);
    return { data: null, error };
  }
};

/**
 * Update task material
 * @param {string} materialId - Task material ID
 * @param {Object} updates - Material updates
 * @returns {Promise<{data, error}>}
 */
export const updateTaskMaterial = async (materialId, updates) => {
  try {
    const { data, error } = await supabase
      .from('task_materials')
      .update(updates)
      .eq('id', materialId)
      .select(`
        *,
        inventory_item:inventory(id, name, unit, sku)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Update task material error:', error);
    return { data: null, error };
  }
};

/**
 * Remove material from task
 * @param {string} materialId - Task material ID
 * @returns {Promise<{data, error}>}
 */
export const removeTaskMaterial = async (materialId) => {
  try {
    const { error } = await supabase
      .from('task_materials')
      .delete()
      .eq('id', materialId);

    if (error) throw error;

    return { data: true, error: null };
  } catch (error) {
    console.error('Remove task material error:', error);
    return { data: null, error };
  }
};

/**
 * Remove material from task
 * @param {string} materialId - Task material ID
 * @returns {Promise<{data, error}>}
 */
export const unlinkTaskMaterial = async (materialId) => {
  try {
    const { error } = await supabase
      .from('task_materials')
      .delete()
      .eq('id', materialId);

    if (error) throw error;

    return { data: true, error: null };
  } catch (error) {
    console.error('Unlink task material error:', error);
    return { data: null, error };
  }
};

/**
 * Get task expenses
 * @param {string} taskId - Task ID
 * @returns {Promise<{data, error}>}
 */
export const getTaskExpenses = async (taskId) => {
  try {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('task_id', taskId)
      .order('expense_date', { ascending: false });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get task expenses error:', error);
    return { data: null, error };
  }
};

/**
 * Link an expense to a task by updating the expense's task_id
 * @param {string} taskId - Task ID
 * @param {string} expenseId - Expense ID
 * @param {number} allocationPercentage - Optional allocation percentage
 * @returns {Promise<{data, error}>}
 */
export const linkExpense = async (taskId, expenseId, allocationPercentage) => {
  try {
    const updates = { task_id: taskId };
    if (allocationPercentage !== undefined) {
      updates.allocation_percentage = allocationPercentage;
    }

    const { data, error } = await supabase
      .from('expenses')
      .update(updates)
      .eq('id', expenseId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Link expense error:', error);
    return { data: null, error };
  }
};

/**
 * Link materials to a task (batch insert)
 * @param {string} taskId - Task ID
 * @param {Array<Object>} materials - Array of material objects
 * @returns {Promise<{data, error}>}
 */
export const linkMaterials = async (taskId, materials) => {
  try {
    const rows = materials.map(m => ({
      task_id: taskId,
      ...m,
    }));

    const { data, error } = await supabase
      .from('task_materials')
      .insert(rows)
      .select(`
        *,
        inventory_item:inventory(id, name, unit, sku)
      `);

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Link materials error:', error);
    return { data: null, error };
  }
};

/**
 * Mark a task as completed
 * @param {string} taskId - Task ID
 * @returns {Promise<{data, error}>}
 */
export const completeTask = async (taskId) => {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .update({
        status: 'completed',
        progress_percentage: 100,
        completed_at: new Date().toISOString(),
      })
      .eq('id', taskId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Complete task error:', error);
    return { data: null, error };
  }
};
